import { Close, MenuBulletHorizontal, Trophy } from '@zoomit/icons';
import { cn, IconButton, Image, Link, localeIRNumberFormatter, Skeleton } from '@zoomit/primitives';
import type { ProductListItemType } from '@zoomit/web-kit';
import { Price, Score } from '@zoomit/web-kit';
import { cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import { useGenerateSingleProductHref } from '../../hooks/catalog/useGenerateSingleProductHref';
import { useOrigin } from '../../providers/OriginProvider';
import AnimatedImage from '../shared/AnimatedImage';

type Props = {
  onClose?: () => void;
  onMore?: () => void;
  isLoadingPrice?: boolean;
  showFeaturedVariant?: boolean;
  eagerLoadImage?: boolean;
} & ProductListItemType;

const ProductListItemMobile = (props: Props) => {
  const {
    image,
    slug,
    title,
    eagerLoadImage = false,
    minPrice,
    score,
    onClose,
    onMore,
    category,
    isLoadingPrice = false,
    isFeatured: isProductFeatured,
    showFeaturedVariant = false,
    isRecommended,
  } = props;

  const isFeatured = isProductFeatured && showFeaturedVariant;
  const { baseMediaUrl } = useOrigin();
  const href = useGenerateSingleProductHref(slug);

  return (
    <Link
      className={cn(
        'bg-card relative isolate flex w-full shrink-0 flex-nowrap items-center overflow-hidden rounded-md shadow',
        {
          'h-auto justify-center': isFeatured,
          'h-32 justify-between': !isFeatured,
        },
      )}
      href={href}
    >
      <div
        className={cn('flex h-full flex-nowrap items-center', {
          'flex-row gap-2 pl-2': !isFeatured,
          'w-full flex-col gap-3': isFeatured,
        })}
      >
        <div
          className={cn('relative flex justify-center bg-white', {
            'aspect-square h-full': !isFeatured,
            'w-full py-4': isFeatured,
          })}
        >
          {eagerLoadImage ? (
            <Image
              className="object-contain"
              width={isFeatured ? 222 : 118}
              height={isFeatured ? 148 : 78}
              alt={image.alt}
              src={`${baseMediaUrl}/${image.id}`}
              sizes={isFeatured ? '256px' : '128px'}
            />
          ) : (
            <AnimatedImage
              className="object-contain"
              width={isFeatured ? 222 : 118}
              height={isFeatured ? 148 : 78}
              alt={image.alt}
              src={`${baseMediaUrl}/${image.id}`}
              placeholder="blur"
              blurDataURL={image.preview}
              sizes={isFeatured ? '256px' : '128px'}
            />
          )}
        </div>
        <div
          className={cn('flex w-full flex-col flex-nowrap items-start justify-between p-3', {
            'h-auto gap-2.5': isFeatured,
            'h-full': !isFeatured,
          })}
        >
          <div className="flex flex-col gap-1.5">
            <div className={cn('flex items-center gap-1', isFeatured ? 'absolute top-2 right-2' : 'static')}>
              {!!score && (
                <div className="mb-0.5 w-fit">
                  <Score score={score} />
                </div>
              )}
              {isRecommended && (
                <div className="mr-1 inline-flex h-5 w-fit items-center gap-1 rounded-full bg-[#F3FFD2] px-2">
                  <Trophy className="fill-primary3" size="sm" />
                  <span className="text-primary3 text-subhead">پیشنهادی</span>
                </div>
              )}
            </div>
            <Typography
              variant={'subhead-bold'}
              lineHeight={2.2}
              className="line-clamp-2 truncate whitespace-normal"
            >
              {category.title} {title}
            </Typography>
          </div>
          {isLoadingPrice && <Skeleton height={1.2} width={11} variant="text" />}
          {!isLoadingPrice && minPrice ? (
            <Price price={localeIRNumberFormatter(minPrice)} />
          ) : (
            <Typography fontSize={1.6} tag="span" lineHeight={1.75} textColor={cssVar(theme.grey3)}>
              ناموجود
            </Typography>
          )}
        </div>
      </div>
      <div className="absolute top-3 left-3">
        {onMore ? (
          <IconButton
            aria-label="more"
            touchTargetInset={12}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onMore();
            }}
          >
            <MenuBulletHorizontal fill={cssVar(theme.grey3)} />
          </IconButton>
        ) : (
          onClose && (
            <IconButton
              aria-label="close"
              className="mb-auto shrink-0"
              touchTargetInset={12}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
            >
              <Close size="lg" />
            </IconButton>
          )
        )}
      </div>
    </Link>
  );
};

export default ProductListItemMobile;
